import { z } from "zod";
import { addDays, timeToMinutes, type IsoDate, type LocalMoment } from "./dates.js";
import { REMINDER_GRACE_MINUTES } from "./schedule.js";
import { isoDateSchema, MAX_DURATION_MINUTES, timeSchema } from "./schemas.js";

/** How far ahead a prep reminder can be. */
export const MAX_PREP_DAYS = 14;

/** Prep reminders go out at this local time on the first prep day. */
export const PREP_REMINDER_TIME = "09:00";

export const taskInputSchema = z.object({
  title: z.string().trim().min(1).max(120),
  emoji: z.string().max(8).nullable().default(null),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default("#0ea5e9"),
  date: isoDateSchema,
  /** null = sometime that day, no fixed time. */
  time: timeSchema.nullable().default(null),
  durationMinutes: z.number().int().min(5).max(MAX_DURATION_MINUTES).default(30),
  notes: z.string().trim().max(1000).nullable().default(null),
  focus: z.boolean().default(false),
  focusMinutes: z.number().int().min(5).max(MAX_DURATION_MINUTES).default(25),
  breakMinutes: z.number().int().min(1).max(MAX_DURATION_MINUTES).default(5),
  /** Only applies when there is a time. */
  reminder: z.boolean().default(true),
  /** Get ready this many days before (e.g. buy the present for Friday's party). */
  prepDaysBefore: z.number().int().min(1).max(MAX_PREP_DAYS).nullable().default(null),
  /** What to get ready; the title is used when empty. */
  prepNote: z.string().trim().max(200).nullable().default(null),
});
export type TaskInput = z.infer<typeof taskInputSchema>;

export interface TaskDto extends TaskInput {
  id: string;
  done: boolean;
}

export const taskDoneSchema = z.object({ done: z.boolean() });

/** Moves the unfinished tasks of one day to another ("pasar a mañana"); all of them, or just `ids`. */
export const moveTasksSchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  ids: z.array(z.string().min(1)).min(1).max(100).optional(),
});
export type MoveTasksInput = z.infer<typeof moveTasksSchema>;

export function minutesToTime(minutes: number): string {
  const m = ((minutes % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

export interface FocusBlock {
  kind: "work" | "break";
  /** Minutes from the start of the task. */
  offset: number;
  minutes: number;
}

/**
 * Splits a focus task into Pomodoro blocks: work, break, work, break… until the duration
 * is used up. It never ends on a break, and a short tail of work joins the last block
 * rather than becoming a block too small to bother with.
 */
export function focusBlocks(t: Pick<TaskInput, "durationMinutes" | "focusMinutes" | "breakMinutes">): FocusBlock[] {
  const blocks: FocusBlock[] = [];
  let offset = 0;
  let left = t.durationMinutes;
  while (left > 0) {
  if (blocks.length > 0) {
    // Not enough left for a break and a worthwhile block: stretch the last work block.
    if (left < t.breakMinutes + 5) {
      blocks[blocks.length - 1]!.minutes += left;
      break;
    }
    blocks.push({ kind: "break", offset, minutes: t.breakMinutes });
    offset += t.breakMinutes;
    left -= t.breakMinutes;
  }
  const minutes = Math.min(t.focusMinutes, left);
  blocks.push({ kind: "work", offset, minutes });
  offset += minutes;
  left -= minutes;
  }
  return blocks;
}

export function isTaskReminderDue(task: { date: IsoDate; time: string | null }, now: LocalMoment): boolean {
  if (task.time === null || task.date !== now.date) return false;
  const late = now.minutes - timeToMinutes(task.time);
  return late >= 0 && late <= REMINDER_GRACE_MINUTES;
}

/** First day the task asks to get ready, or null when it doesn't. */
export function prepStartDate(task: { date: IsoDate; prepDaysBefore: number | null }): IsoDate | null {
  return task.prepDaysBefore ? addDays(task.date, -task.prepDaysBefore) : null;
}

/** Whether `day` falls in the task's prep window: from the first prep day until the day before. */
export function needsPrepOn(task: { date: IsoDate; prepDaysBefore: number | null; done: boolean }, day: IsoDate): boolean {
  const start = prepStartDate(task);
  return start !== null && !task.done && day >= start && day < task.date;
}

export function isPrepReminderDue(task: { date: IsoDate; prepDaysBefore: number | null }, now: LocalMoment): boolean {
  if (prepStartDate(task) !== now.date) return false;
  const late = now.minutes - timeToMinutes(PREP_REMINDER_TIME);
  return late >= 0 && late <= REMINDER_GRACE_MINUTES;
}

/** Most tasks in one proposal: a guard for the server, room for months of packed days. */
export const MAX_PROPOSED_TASKS = 1000;

/** Tasks the assistant proposed for one or more days. Nothing is saved until accepted. */
export interface TaskProposal {
  summary: string;
  tasks: TaskInput[];
}

export const acceptChatTasksSchema = z.object({
  tasks: z.array(taskInputSchema).min(1, "Elige al menos una tarea").max(MAX_PROPOSED_TASKS),
});
